// Exchange Parts — SUPERADMIN manages which part types can be sold with an
// old-part trade-in (exchange). ONE list, obeyed everywhere: supervisors get
// an exchange-price field on these parts, and the counter forms (ops entry +
// the Sale Notes editor) offer their exchange toggle only on these. A part
// matches when one of the active names appears in it, spaces and case
// ignored ("Engine" matches "Engine Assembly").

import React, { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Button, Alert } from '../components/common/index.js';
import { exchangePartsApi } from '../api/exchangePartsApi';
import {
  FiPlus, FiTrash2, FiX, FiRefreshCw, FiRepeat, FiCheckCircle, FiEyeOff,
  FiAlertTriangle,
} from 'react-icons/fi';

const errText = (e, fallback) =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback;

const ExchangePartsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await exchangePartsApi.getList();
      setRows(data?.exchange_parts || []);
    } catch (e) {
      setError(errText(e, 'Failed to load exchange parts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (name.length < 2) { setError('Enter a part name (min 2 characters)'); return; }
    setAdding(true);
    setError(null);
    try {
      await exchangePartsApi.create({ name });
      setSuccess(`"${name}" added — it's now exchange-eligible.`);
      setNewName('');
      await load();
    } catch (e) {
      setError(errText(e, 'Could not add the part'));
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      await exchangePartsApi.update(row.id, { is_active: !row.is_active });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: !r.is_active } : r)));
    } catch (e) {
      setError(errText(e, 'Could not update'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await exchangePartsApi.remove(deleteTarget.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(errText(e, 'Could not delete'));
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = rows.filter((r) => r.is_active).length;

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
              <FiRepeat className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Exchange Parts</h1>
              <p className="mt-0.5 text-sm text-slate-500">
                The part types that can be sold with an old-part trade-in. This one list drives
                everything: supervisors quote an exchange price only on these, and the counter
                forms offer "with exchange" only on these. Match is by name — "Engine" also
                covers "Engine Assembly".
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={load} className="gap-2">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

        {/* Add form */}
        <form onSubmit={add} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-sm font-semibold text-slate-700">Add an exchange-eligible part</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Engine, Gear Box, Converter"
              disabled={adding}
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
            <Button type="submit" variant="primary" loading={adding} className="gap-2">
              <FiPlus className="h-4 w-4" /> Add
            </Button>
          </div>
        </form>

        {/* List */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-bold text-slate-700">
              {rows.length} part type{rows.length === 1 ? '' : 's'}
              <span className="ml-2 text-xs font-medium text-slate-400">{activeCount} active</span>
            </p>
          </div>

          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <FiRepeat className="mx-auto mb-2 h-8 w-8 text-slate-200" />
              <p className="text-sm text-slate-500">No exchange parts yet. Add "Engine", "Gear Box", etc. above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {rows.map((row) => (
                <li key={row.id} className="flex items-center gap-3 px-4 py-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${row.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    <FiRepeat className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm font-bold ${row.is_active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                      {row.name}
                    </p>
                    <p className="text-xs text-slate-400">{row.is_active ? 'Exchange offered' : 'Paused — no exchange field shown'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(row)}
                    disabled={busyId === row.id}
                    title={row.is_active ? 'Pause (hide exchange field)' : 'Activate'}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${row.is_active ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}
                  >
                    {row.is_active ? <><FiCheckCircle className="h-3.5 w-3.5" /> Active</> : <><FiEyeOff className="h-3.5 w-3.5" /> Paused</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(row)}
                    disabled={busyId === row.id}
                    title="Remove"
                    className="rounded-lg border border-rose-100 bg-white p-2 text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setDeleteTarget(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                <FiAlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-base font-bold text-slate-900">Remove "{deleteTarget.name}"?</p>
                <p className="text-xs text-slate-500">Parts named like this stop offering exchange everywhere — supervisor quotes and the counter forms.</p>
              </div>
              <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => setDeleteTarget(null)}>
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="danger" loading={busyId === deleteTarget.id} onClick={confirmDelete}>Remove</Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ExchangePartsPage;
